import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EventBuilderForm } from '@/components/events/EventBuilderForm';
import { AppShell } from '@/components/layout/AppShell';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { DataStatePanel } from '@/components/shared/DataStatePanel';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import { dashboardRoutes, eventRoutes, templateRoutes } from '@/data/routes';
import { cloneTemplateSlotsToParties, createDefaultEventDraft, createEventDraftFromState } from '@/features/events/builder';
import { getTemplateSlotMetrics } from '@/features/templates/builder';
import { createTemplateDraftFromMassDraft } from '@/features/templates/builder';
import { useAuth } from '@/hooks/useAuth';
import { useGuildData } from '@/hooks/useGuildData';
import type { GuildState } from '@/types/app-state';
import type { EventBuilderDraft } from '@/types/domain';
import { fromDateTimeLocalValue, getLocalTimeZoneLabel, toDateTimeLocalValue } from '@/utils/date';

function toFormDraft(draft: EventBuilderDraft): EventBuilderDraft {
  return {
    ...draft,
    startsAt: toDateTimeLocalValue(draft.startsAt),
    signupOpensAt: toDateTimeLocalValue(draft.signupOpensAt),
    signupClosesAt: toDateTimeLocalValue(draft.signupClosesAt),
  };
}

function toPersistedDraft(draft: EventBuilderDraft): EventBuilderDraft {
  return {
    ...draft,
    startsAt: fromDateTimeLocalValue(draft.startsAt),
    signupOpensAt: fromDateTimeLocalValue(draft.signupOpensAt),
    signupClosesAt: fromDateTimeLocalValue(draft.signupClosesAt),
  };
}

function hasBuilderSlots(draft: EventBuilderDraft): boolean {
  return draft.parties.some((party) => party.slots.length > 0);
}

function draftUsesSelectedTemplateLayout(draft: EventBuilderDraft, templateId?: string): boolean {
  if (!templateId) {
    return false;
  }

  return draft.parties.some((party) => party.slots.some((slot) => slot.templateSlotId));
}

function applyTemplateLayoutToDraft(draft: EventBuilderDraft, templateId: string, state: GuildState): EventBuilderDraft {
  return {
    ...draft,
    templateId,
    parties: cloneTemplateSlotsToParties(templateId, state),
  };
}

export function EventBuilderPage(): JSX.Element {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = eventId ? 'edit' : 'create';
  const { hasAnyRole } = useAuth();
  const {
    state,
    isLoading,
    error,
    refreshData,
    saveEventBuilder,
    quickCreateEventFromTemplate,
    archiveEvent,
    deleteEventPermanently,
  } = useGuildData();
  const canManageTemplates = hasAnyRole(['admin', 'officer']);
  const canDeletePermanently = hasAnyRole(['admin']);
  const existingEvent = useMemo(() => state.events.find((event) => event.id === eventId), [eventId, state.events]);
  const locationState = location.state as { fromMassDraft?: EventBuilderDraft } | null;
  const templateIdFromQuery = searchParams.get('templateId') ?? undefined;
  const joinedCountBySlotId = useMemo<Record<string, number>>(
    () =>
      state.signups
        .filter((signup) => signup.status === 'joined' && (!eventId || signup.eventId === eventId))
        .reduce<Record<string, number>>((counts, signup) => {
          counts[signup.eventSlotId] = (counts[signup.eventSlotId] ?? 0) + 1;
          return counts;
        }, {}),
    [eventId, state.signups],
  );
  const [draft, setDraft] = useState<EventBuilderDraft>(() => toFormDraft(createDefaultEventDraft()));
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoadTemplateDialogOpen, setIsLoadTemplateDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [appliedTemplateId, setAppliedTemplateId] = useState<string | undefined>();
  const createBootstrapKeyRef = useRef<string | null>(null);
  const timeZoneLabel = getLocalTimeZoneLabel();
  const allSlots = draft.parties.flatMap((party) => party.slots);
  const selectedTemplate = useMemo(
    () => state.templates.find((template) => template.id === draft.templateId) ?? null,
    [draft.templateId, state.templates],
  );
  const selectedTemplateMetrics = useMemo(
    () =>
      selectedTemplate
        ? getTemplateSlotMetrics(state.templateSlots.filter((slot) => slot.templateId === selectedTemplate.id))
        : null,
    [selectedTemplate, state.templateSlots],
  );
  const builderHasSlots = hasBuilderSlots(draft);
  const isTemplateLayoutApplied = Boolean(draft.templateId && appliedTemplateId === draft.templateId && builderHasSlots);

  useEffect(() => {
    if (mode !== 'edit' || !existingEvent) {
      return;
    }

    if (draft.id === existingEvent.id) {
      return;
    }

    const nextDraft = toFormDraft(createEventDraftFromState(state, existingEvent));
    setDraft(nextDraft);
    setAppliedTemplateId(nextDraft.templateId ?? undefined);

    setFormError(null);
  }, [draft.id, existingEvent, mode, state.eventParties, state.eventSlots]);

  useEffect(() => {
    if (mode !== 'create') {
      return;
    }

    if (locationState?.fromMassDraft) {
      const bootstrapKey = `${location.key}:mass-draft`;

      if (createBootstrapKeyRef.current === bootstrapKey) {
        return;
      }

      const nextDraft = toFormDraft(locationState.fromMassDraft);
      setDraft(nextDraft);
      setAppliedTemplateId(draftUsesSelectedTemplateLayout(nextDraft, nextDraft.templateId) ? nextDraft.templateId : undefined);
      setFormError(null);
      createBootstrapKeyRef.current = bootstrapKey;
      return;
    }

    if (!templateIdFromQuery) {
      return;
    }

    if (!state.templateSlots.some((slot) => slot.templateId === templateIdFromQuery)) {
      return;
    }

    const bootstrapKey = `${location.key}:template:${templateIdFromQuery}`;

    if (createBootstrapKeyRef.current === bootstrapKey) {
      return;
    }

    const nextDraft = toFormDraft(applyTemplateLayoutToDraft(createDefaultEventDraft(), templateIdFromQuery, state));
    setDraft(nextDraft);
    setAppliedTemplateId(templateIdFromQuery);
    setFormError(null);
    createBootstrapKeyRef.current = bootstrapKey;
  }, [location.key, locationState, mode, state.templateSlots, templateIdFromQuery]);

  if (isLoading && mode === 'edit' && !existingEvent) {
    return (
      <AppShell>
        <DataStatePanel title="Loading mass setup" description="Fetching the mass structure, parties, and slot data." />
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <DataStatePanel title="Unable to load mass setup" description={error} actionLabel="Retry" onAction={() => void refreshData()} />
      </AppShell>
    );
  }

  if (mode === 'edit' && !existingEvent) {
    return (
      <AppShell>
        <EmptyState title="Mass not found" description="This event no longer exists or is not available to your account." />
      </AppShell>
    );
  }

  const applySelectedTemplateLayout = () => {
    if (!draft.templateId) {
      setFormError('Choose a template before applying its saved layout.');
      return;
    }

    if (!state.templateSlots.some((slot) => slot.templateId === draft.templateId)) {
      setFormError('This template does not have any saved slots to apply yet.');
      return;
    }

    setDraft((currentDraft) => applyTemplateLayoutToDraft(currentDraft, draft.templateId!, state));
    setAppliedTemplateId(draft.templateId);
    setFormError(null);
  };

  const handleApplyTemplateLayout = () => {
    if (!draft.templateId) {
      setFormError('Choose a template before applying its saved layout.');
      return;
    }

    if (builderHasSlots) {
      setIsLoadTemplateDialogOpen(true);
      return;
    }

    applySelectedTemplateLayout();
  };

  const handleSave = async () => {
    setFormError(null);

    if (!draft.title.trim()) {
      setFormError('Mass title is required.');
      return;
    }

    if (draft.templateId && draft.templateId !== appliedTemplateId) {
      setFormError('Apply the selected template layout or clear the template selection before saving this mass.');
      return;
    }

    if (!draft.signupOpensAt || !draft.signupClosesAt || !draft.startsAt) {
      setFormError('Signup open, signup close, and start time are all required.');
      return;
    }

    if (new Date(draft.signupOpensAt).getTime() > new Date(draft.signupClosesAt).getTime()) {
      setFormError('Signup open time must be earlier than signup close time.');
      return;
    }

    if (new Date(draft.signupClosesAt).getTime() > new Date(draft.startsAt).getTime()) {
      setFormError('Signup close time must be earlier than or equal to the mass start time.');
      return;
    }

    if (allSlots.some((slot) => !slot.weaponId || !slot.weaponName || !slot.imagePath)) {
      setFormError('Every configured slot needs a weapon selected from the catalog.');
      return;
    }

    setIsSaving(true);

    try {
      const eventKey = await saveEventBuilder(toPersistedDraft(draft));

      if (!eventId && eventKey) {
        navigate(eventRoutes.edit(eventKey), { replace: true });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickCreate = async () => {
    setFormError(null);

    if (!draft.templateId) {
      setFormError('Choose a template before creating directly from its saved layout.');
      return;
    }

    if (!draft.title.trim()) {
      setFormError('Mass title is required before creating from a template.');
      return;
    }

    if (!draft.signupOpensAt || !draft.signupClosesAt || !draft.startsAt) {
      setFormError('Template quick create still needs signup and start times.');
      return;
    }

    setIsSaving(true);

    try {
      const nextEventId = await quickCreateEventFromTemplate({
        templateId: draft.templateId,
        title: draft.title,
        description: draft.description,
        startsAt: fromDateTimeLocalValue(draft.startsAt),
        signupOpensAt: fromDateTimeLocalValue(draft.signupOpensAt),
        signupClosesAt: fromDateTimeLocalValue(draft.signupClosesAt),
      });

      if (nextEventId) {
        navigate(eventRoutes.edit(nextEventId), { replace: true });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!eventId) {
      return;
    }

    const confirmed = window.confirm('Archive this mass now? This keeps history but closes active management.');

    if (!confirmed) {
      return;
    }

    setIsArchiving(true);

    try {
      await archiveEvent(eventId, true);
      navigate(dashboardRoutes.home);
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        title={mode === 'create' ? 'Create Mass' : `Edit Mass: ${existingEvent?.title ?? 'Mass'}`}
        description={`Party 1 to Party 4 builder. ${timeZoneLabel}.`}
        actions={
          <Link to={dashboardRoutes.home} className="button button--secondary">
            Back to Dashboard
          </Link>
        }
      />

      {formError ? <DataStatePanel title="Fix the mass setup" description={formError} /> : null}

      <EventBuilderForm
        mode={mode}
        draft={draft}
        templates={state.templates}
        weapons={state.weapons}
        joinedCountBySlotId={joinedCountBySlotId}
        isSaving={isSaving}
        isArchiving={isArchiving}
        isDeleting={isDeleting}
        canManageTemplates={canManageTemplates}
        canDeletePermanently={canDeletePermanently}
        timeZoneLabel={timeZoneLabel}
        selectedTemplate={selectedTemplate}
        selectedTemplateMetrics={selectedTemplateMetrics}
        isTemplateLayoutApplied={isTemplateLayoutApplied}
        onDraftChange={setDraft}
        onTemplateChange={(templateId) => {
          setDraft((currentDraft) => ({
            ...currentDraft,
            templateId,
          }));

          if (!templateId) {
            setAppliedTemplateId(undefined);
          }

          setFormError(null);
        }}
        onApplyTemplateLayout={handleApplyTemplateLayout}
        onQuickCreateFromTemplate={handleQuickCreate}
        onSave={handleSave}
        onSaveAsTemplate={
          canManageTemplates
            ? () =>
                navigate(templateRoutes.create, {
                  state: {
                    initialTemplateDraft: createTemplateDraftFromMassDraft(draft),
                    skipStoredCreateDraft: true,
                    sourceEventId: draft.id,
                    sourceTemplateId: draft.templateId,
                  },
                })
            : undefined
        }
        onArchive={mode === 'edit' ? handleArchive : undefined}
        onDelete={
          mode === 'edit' && canDeletePermanently
            ? () => {
                setDeleteConfirmation('');
                setIsDeleteDialogOpen(true);
              }
            : undefined
        }
      />

      <ConfirmationDialog
        open={isLoadTemplateDialogOpen}
        title="Replace Builder With Template"
        description="Applying this saved template will replace Party 1 through Party 4 in the current mass builder. This is the safest way to keep the mass aligned with the saved template layout."
        confirmLabel="Apply Template Layout"
        confirmVariant="danger"
        cancelLabel="Cancel"
        secondaryActionLabel={canManageTemplates ? 'Save Current as Template First' : undefined}
        secondaryActionVariant="secondary"
        confirmationValue=""
        onConfirmationValueChange={() => undefined}
        onClose={() => {
          setIsLoadTemplateDialogOpen(false);
        }}
        onSecondaryAction={
          canManageTemplates
            ? () => {
                setIsLoadTemplateDialogOpen(false);
                navigate(templateRoutes.create, {
                  state: {
                    initialTemplateDraft: createTemplateDraftFromMassDraft(draft),
                    skipStoredCreateDraft: true,
                    sourceEventId: draft.id,
                    sourceTemplateId: draft.templateId,
                  },
                });
              }
            : undefined
        }
        onConfirm={() => {
          applySelectedTemplateLayout();
          setIsLoadTemplateDialogOpen(false);
        }}
      />

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        title="Delete Mass"
        description="Are you sure you want to permanently delete this mass? This removes its parties, slots, signups, and related live references. This cannot be undone."
        confirmLabel="Delete Mass"
        confirmVariant="danger"
        confirmationLabel="Type the mass title to confirm"
        requiredText={existingEvent?.title ?? ''}
        confirmationValue={deleteConfirmation}
        onConfirmationValueChange={setDeleteConfirmation}
        isProcessing={isDeleting}
        onClose={() => {
          if (isDeleting) {
            return;
          }

          setIsDeleteDialogOpen(false);
          setDeleteConfirmation('');
        }}
        onConfirm={() => {
          if (!eventId) {
            return;
          }

          setIsDeleting(true);

          void deleteEventPermanently(eventId, deleteConfirmation)
            .then((wasDeleted) => {
              if (!wasDeleted) {
                return;
              }

              setIsDeleteDialogOpen(false);
              setDeleteConfirmation('');
              navigate(dashboardRoutes.home, { replace: true });
            })
            .finally(() => {
              setIsDeleting(false);
            });
        }}
      />
    </AppShell>
  );
}
